import React, { useEffect, useState } from "react";
import type { PaperPricingBasis, SheetSizeSpecDto } from "../../../../domain/types/company/company-product-system";
import { companyProductSystemApiService } from "../../../../infrastructure/api/company/company-product-system-api-service";
import { companyProductSystemUseCase } from "../../../../di/container";

export interface ImpositionSettings {
  paperPricingBasis: PaperPricingBasis;
  impositionSheetSizeSpecId: number | null;
  impositionGapMm: number;
}

interface ImpositionSettingsCardProps {
  companyId: number;
  productId: number;
  initial: ImpositionSettings;
  onSaved: (settings: ImpositionSettings) => void;
  notify: (message: string) => void;
}

const BASIS_OPTIONS: { value: PaperPricingBasis; title: string; description: string }[] = [
  {
    value: "SIZE_FIT",
    title: "기존 기준",
    description: "작업 사이즈가 들어가는 용지 중 가장 싼 규격 1장에 1개로 계산합니다.",
  },
  {
    value: "IMPOSITION",
    title: "조판 기준",
    description: "기준 용지 한 장에 몇 개가 앉는지로 필요 장수를 구해 용지값·인쇄비를 장수로 계산합니다.",
  },
];

/**
 * 낱장 상품의 용지 가격 기준 (2026-10-26 대한DH 요청)
 *
 * 조판 기준을 고르면 기준 용지 규격과 작업물 간격을 정한다.
 * 연결된 용지에 기준 규격 가격이 없으면 견적식 미리보기에 그 사유가 나온다.
 */
export const ImpositionSettingsCard = ({ companyId, productId, initial, onSaved, notify }: ImpositionSettingsCardProps) => {
  const [basis, setBasis] = useState<PaperPricingBasis>(initial.paperPricingBasis);
  const [specId, setSpecId] = useState<number | null>(initial.impositionSheetSizeSpecId);
  const [gapMm, setGapMm] = useState<number>(initial.impositionGapMm);
  const [specs, setSpecs] = useState<SheetSizeSpecDto[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBasis(initial.paperPricingBasis);
    setSpecId(initial.impositionSheetSizeSpecId);
    setGapMm(initial.impositionGapMm);
  }, [initial.paperPricingBasis, initial.impositionSheetSizeSpecId, initial.impositionGapMm]);

  useEffect(() => {
    let cancelled = false;
    companyProductSystemApiService
      .getSheetSizeSpecs(companyId)
      .then((response) => {
        if (!cancelled) setSpecs(response.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setSpecs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const changed =
    basis !== initial.paperPricingBasis ||
    specId !== initial.impositionSheetSizeSpecId ||
    gapMm !== initial.impositionGapMm;
  const gapValid = Number.isInteger(gapMm) && gapMm >= 0 && gapMm <= 50;
  const canSave = changed && gapValid && (basis !== "IMPOSITION" || specId != null) && !saving;

  const save = async () => {
    setSaving(true);
    try {
      await companyProductSystemUseCase.updateProduct(companyId, productId, {
        paperPricingBasis: basis,
        impositionSheetSizeSpecId: specId,
        impositionGapMm: gapMm,
      });
      onSaved({ paperPricingBasis: basis, impositionSheetSizeSpecId: specId, impositionGapMm: gapMm });
      notify(basis === "IMPOSITION" ? "조판 기준으로 계산합니다." : "기존 기준으로 계산합니다.");
    } catch (error: any) {
      notify(error?.message || "용지 가격 기준을 저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mb-12 text-left">
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-lg font-semibold">용지 가격 기준</h2>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          className="px-5 py-2 text-[13px] font-medium rounded-[8px] bg-[#222] text-white hover:bg-[#444] disabled:bg-[#e8e8e8] disabled:text-[#aaa]"
        >
          {saving ? "저장 중..." : "저장"}
        </button>
      </div>
      <p className="text-[13px] text-[#888] mb-4">낱장 상품의 용지값과 인쇄비를 어떤 기준으로 셀지 정합니다.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {BASIS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setBasis(option.value)}
            aria-pressed={basis === option.value}
            className={`text-left p-4 rounded-[12px] border transition-colors ${
              basis === option.value ? "border-[#4285F4] ring-2 ring-[#4285F4]/20 bg-[#E8EFFF]/40" : "border-[#e0e0e0] hover:bg-[#fafafa]"
            }`}
          >
            <span className={`block text-[15px] font-medium ${basis === option.value ? "text-[#4285F4]" : "text-[#222]"}`}>
              {option.title}
            </span>
            <span className="block mt-1 text-[13px] text-[#555] leading-[1.5]">{option.description}</span>
          </button>
        ))}
      </div>

      {basis === "IMPOSITION" && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">조판 기준 용지</span>
            <select
              value={specId ?? ""}
              onChange={(e) => setSpecId(e.target.value ? Number(e.target.value) : null)}
              className="h-[40px] px-3 border border-[#e0e0e0] rounded-[8px] text-[14px] bg-white focus:border-[#4285F4] focus:outline-none"
            >
              <option value="">용지 규격을 고르세요</option>
              {specs.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name} ({spec.width}×{spec.height}mm)
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">작업물 간격 (mm)</span>
            <input
              type="number"
              min={0}
              max={50}
              step={1}
              value={gapMm}
              onChange={(e) => setGapMm(e.target.value === "" ? 0 : Number(e.target.value))}
              className={`h-[40px] px-3 border rounded-[8px] text-[14px] focus:outline-none ${
                gapValid ? "border-[#e0e0e0] focus:border-[#4285F4]" : "border-[#F24A4A]"
              }`}
            />
          </label>
          <p className="md:col-span-2 text-[12px] text-[#888] leading-[1.6]">
            작업물 하나에 간격을 더한 크기로 기준 용지에 몇 개가 앉는지 셉니다(정방향·회전 중 많은 쪽). 상품에 연결된 용지마다 이 규격의
            가격이 있어야 하며, 없으면 견적식 미리보기에 사유가 표시됩니다. 이 설정은 셀프 디자인(Studio)에도 함께 전달됩니다.
          </p>
        </div>
      )}
    </div>
  );
};
